// «Помощь» союзников: плавающая кнопка «🤝 Помочь» (когда союзники просят помощи) и кнопка
// «Попросить помощи» в окне ускорения таймера (стройка, исследование, найм).
import { post } from '@/api/client';
import { onSocket } from '@/api/socket';
import { haptic } from '@/lib/device';
import { errorText } from '@/lib/errors';
import { useApi } from '@/lib/hooks';
import { useGame } from '@/store/game';
import { Button, cx } from '@/ui';
import { ALLIANCE_HELP, type HelpKind } from '@sk/shared';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface HelpRow {
  id: string;
  userId: string;
  nickname: string | null;
  kind: HelpKind;
  targetId: string;
  label: Record<string, unknown>;
  helps: number;
  maxHelps: number;
  stepSec: number;
  mine: boolean;
  helped: boolean;
}

interface HelpList {
  inAlliance: boolean;
  canHelp: HelpRow[];
  mine: HelpRow[];
}

/** Запросы помощи альянса с обновлением по сокету. */
export function useHelps() {
  const api = useApi<HelpList>('/alliance/helps');
  const { reload } = api;
  useEffect(() => onSocket('alliance:help', () => void reload()), [reload]);
  return api;
}

/** Плавающая кнопка: помочь всем союзникам одним нажатием. */
export function AllianceHelpFab() {
  const { t } = useTranslation();
  const toast = useGame((s) => s.toast);
  const { data, reload } = useHelps();
  const [busy, setBusy] = useState(false);
  const n = data?.canHelp.length ?? 0;
  if (!n) return null;
  const helpAll = async () => {
    setBusy(true);
    try {
      const r = await post<{ helped: number; reward: number }>('/alliance/help/give', {});
      haptic(20);
      toast(
        r.reward > 0
          ? t('game:help.doneReward', { n: r.helped, silver: r.reward })
          : t('game:help.done', { n: r.helped }),
        'success',
      );
    } catch (e) {
      toast(errorText(e), 'error');
    } finally {
      setBusy(false);
      void reload();
    }
  };
  return (
    <button
      type="button"
      disabled={busy}
      onClick={helpAll}
      aria-label={t('game:help.fab', { n })}
      className={cx(
        'fixed bottom-[calc(84px+env(safe-area-inset-bottom))] left-3 z-30 flex h-14 w-14 items-center justify-center',
        'rounded-full border-2 border-gold-300 bg-gold-500 text-3xl shadow-xl active:scale-95',
        !busy && 'animate-bounce',
      )}
    >
      🤝
      <span className="absolute -right-1 -top-1 min-w-5 rounded-full bg-blood-500 px-1 text-xs font-bold text-white">
        {n}
      </span>
    </button>
  );
}

/** Кнопка в окне ускорения: попросить союзников помочь с этим таймером / сколько уже помогли. */
export function RequestHelpButton({ kind, targetId }: { kind: HelpKind; targetId: string }) {
  const { t } = useTranslation();
  const toast = useGame((s) => s.toast);
  const { data, reload } = useHelps();
  const [busy, setBusy] = useState(false);
  if (!data?.inAlliance) return null;
  const req = data.mine.find((h) => h.kind === kind && h.targetId === targetId);
  if (req)
    return (
      <p className="rounded-lg bg-ink-800 p-2 text-center text-sm text-moss-400">
        🤝 {t('game:help.status', { n: req.helps, max: req.maxHelps })}
      </p>
    );
  return (
    <Button
      variant="secondary"
      className="w-full"
      loading={busy}
      onClick={async () => {
        setBusy(true);
        try {
          await post('/alliance/help/request', { kind, targetId });
          toast(t('game:help.requested'), 'success');
          await reload();
        } catch (e) {
          toast(errorText(e), 'error');
        } finally {
          setBusy(false);
        }
      }}
    >
      🤝 {t('game:help.request', { pct: ALLIANCE_HELP.pct })}
    </Button>
  );
}
